import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import EmbeddedClinicList from "../../components/EmbeddedClinicList";

const faqs = [
  {
    question: "Why would my pet need an X-ray?",
    answer:
      "X-rays let vets look at what's happening beneath the skin, bones, joints, and organs, without surgery. They're commonly used after injuries, for swallowed objects, or to investigate ongoing symptoms like limping or breathing trouble.",
  },
  {
    question: "Is an X-ray safe for pets?",
    answer:
      "Yes. Pet X-rays use a very low dose of radiation and are considered safe for pets of all ages, including puppies, kittens, and senior pets. Clinics follow standard safety protocols throughout the procedure.",
  },
  {
    question: "Does my pet need sedation for an X-ray?",
    answer:
      "Not always. It depends on your pet's temperament and the area being scanned. Nervous or restless pets may need mild sedation so the images come out clear and accurate.",
  },
  {
    question: "How long does a pet X-ray take?",
    answer:
      "The actual scan takes only a few minutes. However, with positioning, calming your pet, and reviewing the results, you should expect to spend around 30–45 minutes at the clinic.",
  },
  {
    question: "What conditions can be diagnosed with an X-ray?",
    answer:
      "X-rays can detect fractures, dislocations, swallowed objects, bladder stones, certain tumours, lung issues, and joint problems, giving your vet a clearer picture of your pet's condition.",
  },
  {
    question: "How much does a pet X-ray cost in Bangalore?",
    answer:
      "Pet X-ray cost varies depending on the body part scanned, the number of images required, and whether sedation is needed. Through PetQura, finding an affordable pet X-ray Bangalore option is simple, since our partner clinics share estimated costs upfront before the procedure.",
  },
  {
    question: "Can I find a pet X-ray near me through PetQura?",
    answer:
      "Yes. Simply share your location and the service you're looking for, and PetQura will show you the nearest pet X-ray clinic options in Bangalore, along with appointment availability.",
  },
  {
    question: "How do I book a pet X-ray appointment through PetQura?",
    answer:
      "Just search for the service on our website, choose a clinic near you, and pick a convenient time slot. It's quick, simple, and designed to save you time when your pet needs care.",
  },
];

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqRow>
      <FaqQuestion onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaqIcon>{open ? "−" : "+"}</FaqIcon>
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

const PetXray = () => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Pet X-Ray Near You in Bangalore | PetQura</title>
        <meta
          name="description"
          content="Find pet X-ray clinics near you in Bangalore. Browse verified veterinary diagnostic centres offering pet X-ray services and book an appointment online with ease."
        />
      </Helmet>

      <PageWrapper>
        <HeroSection>
          <h1>Pet X-Ray Clinics Near You in Bangalore by PetQura</h1>
          <EmbeddedClinicList serviceId={null} previewMode />
          <p>
            When our pets get hurt, hide their pain, or start behaving
            differently, we want answers fast. That's exactly where an X-ray
            comes in. If you've searched <em>pet x ray near me</em>, you're
            probably looking for a quick, reliable way to understand what's
            going on inside your furry friend without added stress. At PetQura,
            we help pet parents across the city find the nearest veterinary
            clinics offering X-ray services, so you don't have to spend hours
            calling around or driving from one clinic to another.
          </p>
          <p>
            A pet X-ray gives vets a clear picture of your pet's bones, joints,
            and internal organs, helping them spot fractures, blockages,
            tumours, or other issues that aren't visible from the outside.
            Whether it's an emergency or a routine check, we make it simple to
            find pet X-ray Bangalore clinics that suit your pet's needs and
            your schedule.
          </p>
        </HeroSection>

        {/* ── When Your Pet May Need an X-Ray ── */}
        <ContentSection>
          <SectionHeading>When Your Pet May Need an X-Ray</SectionHeading>
          <p>
            Pets can't tell us where it hurts, so we rely on their behaviour
            and a vet's trained eye to know when something's wrong. Here are
            some common reasons an X-ray might be recommended:
          </p>
          <BulletList>
            <li>Limping, favouring a leg, or reluctance to move</li>
            <li>A recent fall, accident, or suspected fracture</li>
            <li>Swallowing a toy, bone, or foreign object</li>
            <li>Persistent coughing or laboured breathing</li>
            <li>Unexplained swelling or lumps</li>
            <li>Ongoing digestive issues or vomiting</li>
          </BulletList>
          <p>
            If you've noticed any of these signs, searching for{" "}
            <em>dog x rays near me</em> is a good first step. Cats and smaller
            pets deserve the same careful attention too. Through PetQura, you
            can quickly locate a pet clinic near you and get your pet checked
            without delay.
          </p>
        </ContentSection>

        {/* ── What to Expect ── */}
        <ContentSection>
          <SectionHeading>
            What to Expect During the Pet X-Ray
          </SectionHeading>
          <p>
            We know that any mention of a "procedure" can feel worrying, but a
            pet X-ray is usually quick, painless, and over before your pet even
            realises what happened. Your pet is positioned carefully on a table
            while the machine captures images of the required area, and most
            pets only need to stay still for a few seconds at a time.
          </p>
          <p>
            Sedation isn't always necessary — it usually depends on your pet's
            temperament, the area being scanned, and how cooperative they're
            feeling that day. Anxious or wriggly pets may be given mild
            sedation to keep them calm and to make sure the images come out
            clear. Once it's done, your vet will review the X-ray with you and
            explain what it shows, so you're never left guessing about your
            pet's health.
          </p>
        </ContentSection>

        {/* ── Cost ── */}
        <ContentSection>
          <SectionHeading>Pet X-Ray Cost</SectionHeading>
          <p>
            We get asked about <em>pet x ray cost</em> quite often, and
            honestly, it depends on a few factors. The area being scanned, the
            number of images needed, whether sedation is required, and the
            clinic's location all play a role in the final price. A single view
            X-ray for a paw will usually cost less than a full-body scan for
            internal injuries.
          </p>
          <p>
            We believe pet parents deserve transparency, so when you book
            through PetQura, the partnering dog X-ray clinic will walk you
            through the expected cost before the procedure begins. This way,
            there are no surprises, and you can make an informed decision for
            your pet without financial stress clouding an already worrying
            situation.
          </p>
        </ContentSection>

        {/* ── Finding Near You ── */}
        <ContentSection>
          <SectionHeading>
            Finding a Pet X-Ray Near You, the Easy Way
          </SectionHeading>
          <p>
            Searching endlessly for a <em>pet x ray near you</em> shouldn't
            add to your worry when your pet is already unwell. That's the
            entire idea behind PetQura. We connect you with veterinary clinics
            across Bangalore based on the exact service you need, X-rays
            included, so you can book an appointment within minutes. Just tell
            us your location and what your pet needs, and we'll show you the
            closest, most suitable pet X-ray near you. No endless scrolling, no
            confusing phone calls, just a straightforward way to get your pet
            the care they need, faster. Because when it comes to your pet's
            health, every minute saved matters, for both of you.
          </p>
        </ContentSection>

        {/* ── FAQ ── */}
        <ContentSection>
          <SectionHeading>Frequently Asked Questions (FAQs)</SectionHeading>
          <FaqList>
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </FaqList>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default PetXray;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5% 5rem;

  @media (max-width: 768px) {
    padding: 2rem 5% 4rem;
  }
`;

const HeroSection = styled.section`
  margin-bottom: 3rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1.5rem;
    font-family: Montserrat, sans-serif;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const ContentSection = styled.section`
  margin-bottom: 2.5rem;

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BulletList = styled.ul`
  padding-left: 1.4rem;
  margin-bottom: 1rem;

  li {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 0.6rem;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f7f9ff;
  }
`;

const FaqIcon = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #00419d;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1rem;
  font-size: 15px;
  line-height: 1.75;
  color: #555555;
  font-family: Montserrat, sans-serif;
  background: #fafafa;
`;
